import { type ReactNode, useCallback, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { z } from 'zod';

import { useGetProjectById } from '@/api/gen/projects';
import type { TLProjectRole } from '@/api/gen/tessera-backend-api.schemas';
import { FunctionalSpecsTab } from '@/features/workbench/functional-specs/FunctionalSpecsTab';
import { useRecentProjects } from '@/features/workbench/hooks/use-recent-projects';
import { LandscapeSyncTab } from '@/features/workbench/landscape-sync/LandscapeSyncTab';
import { ObjectsTab } from '@/features/workbench/project/ObjectsTab';
import { ProgramsTab } from '@/features/workbench/project/ProgramsTab';
import { ProjectTab, resolveProjectTab, tabsForProjectType } from '@/features/workbench/project/project-tabs';
import { ProjectMembersTab } from '@/features/workbench/project/ProjectMembersTab';
import { ProjectSettingsTab } from '@/features/workbench/project/ProjectSettingsTab';
import { RulesAndSkillsTab } from '@/features/workbench/project/RulesAndSkillsTab';
import { useLocalize } from '@/hooks/use-localize';
import { Button } from '@/ui/Button';
import { PanelTabs } from '@/ui/panel-tabs/PanelTabs';
import { SkeletonLoader } from '@/ui/skeleton/SkeletonLoader';

const ProjectRouteTabs = PanelTabs<ProjectTab>;

const TAB_TRIGGER_CLASS =
  'rounded-md px-4 py-1.5 text-sm font-medium text-white/60 transition-colors data-[state=active]:bg-white/10 data-[state=active]:text-white';

export const Route = createFileRoute('/_protected/t/$tenantId/workbench/project/$projectId')({
  component: WorkbenchProjectRoute,
  validateSearch: z.object({
    tab: z.nativeEnum(ProjectTab).optional().catch(undefined),
  }),
});

type TabRenderProps = { projectId: string; myRole: TLProjectRole | undefined };

/**
 * One definition per tab, so a registry entry in `project-tabs.ts` cannot name a tab whose
 * content nobody renders. The `Record` is total on `ProjectTab` — adding an enum member without
 * a definition here is a type error, not a blank panel.
 */
type TabDefinition = {
  label: (t: ReturnType<typeof useLocalize>['tLocalize']) => ReactNode;
  contentClass: string;
  render: (props: TabRenderProps) => ReactNode;
};

const TABS: Record<ProjectTab, TabDefinition> = {
  [ProjectTab.PROGRAMS]: {
    label: () => 'Programs',
    contentClass: 'flex-1 overflow-auto p-6',
    render: ({ projectId }) => <ProgramsTab projectId={projectId} />,
  },
  [ProjectTab.OBJECTS]: {
    label: () => 'Objects',
    // The objects table manages its own scrolling, as Landscape Sync does.
    contentClass: 'flex-1 overflow-hidden',
    render: ({ projectId }) => <ObjectsTab projectId={projectId} />,
  },
  [ProjectTab.SPECS]: {
    label: () => 'Functional Specs',
    contentClass: 'flex-1 overflow-auto p-6',
    render: ({ projectId }) => <FunctionalSpecsTab projectId={projectId} />,
  },
  [ProjectTab.RULES_AND_SKILLS]: {
    label: (t) => t('editor.workbench.rulesAndSkills.tabLabel'),
    contentClass: 'flex-1 overflow-auto p-6',
    render: ({ projectId }) => <RulesAndSkillsTab projectId={projectId} />,
  },
  [ProjectTab.LANDSCAPE_SYNC]: {
    label: () => 'Landscape Sync',
    // Landscape Sync manages its own scrolling.
    contentClass: 'flex-1 overflow-hidden',
    render: ({ projectId }) => <LandscapeSyncTab projectId={projectId} />,
  },
  [ProjectTab.SETTINGS]: {
    label: () => 'Settings',
    contentClass: 'flex-1 overflow-auto p-6',
    render: ({ projectId }) => <ProjectSettingsTab projectId={projectId} />,
  },
  [ProjectTab.MEMBERS]: {
    label: () => 'Members',
    contentClass: 'flex-1 overflow-auto p-6',
    render: ({ projectId, myRole }) => <ProjectMembersTab projectId={projectId} myRole={myRole} />,
  },
};

function WorkbenchProjectRoute() {
  const { tenantId, projectId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { tLocalize } = useLocalize();

  const { addRecentProject } = useRecentProjects();
  const { data: project, isLoading: isProjectLoading } = useGetProjectById({ projectId });
  const tabs = tabsForProjectType(project?.type);
  const activeTab = resolveProjectTab(tabs, tab);

  const handleTabChange = useCallback(
    (next: ProjectTab) => {
      navigate({
        to: '/t/$tenantId/workbench/project/$projectId',
        params: { tenantId, projectId },
        // The type's first tab is the one that gets a clean URL, whichever tab that turns out
        // to be — `tabs` is a module constant per type, so it is a stable dependency.
        search: next === tabs[0] ? {} : { tab: next },
      });
    },
    [navigate, projectId, tabs, tenantId],
  );

  useEffect(() => {
    addRecentProject(projectId);
  }, [addRecentProject, projectId]);

  if (isProjectLoading) {
    return (
      <div className="flex size-full flex-col">
        <div className="flex items-center justify-between border-b border-white/10 p-6">
          <SkeletonLoader className="h-8 w-48 rounded-sm" />
        </div>
        <div className="flex flex-1 flex-col gap-3 p-6">
          <SkeletonLoader className="h-10 w-full rounded-sm" />
          <SkeletonLoader className="h-10 w-full rounded-sm" />
          <SkeletonLoader className="h-10 w-full rounded-sm" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex size-full flex-col">
        <div className="flex items-center justify-between border-b border-white/10 p-6">
          <h1 className="text-2xl font-semibold text-white">Project not found</h1>
        </div>
        <div className="flex-1 p-6">
          <Button onClick={() => navigate({ to: '/t/$tenantId/workbench', params: { tenantId } })}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Workbench
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex size-full flex-col">
      <div className="flex items-center justify-between border-b border-white/10 p-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/t/$tenantId/workbench', params: { tenantId } })}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <h1 className="text-2xl font-semibold text-white">{project.name}</h1>
        </div>
      </div>

      <ProjectRouteTabs tab={activeTab} onTabChange={handleTabChange} className="flex flex-1 flex-col overflow-hidden">
        <div className="border-b border-white/10 px-6">
          <ProjectRouteTabs.List className="flex gap-2 py-3">
            {tabs.map((id) => (
              <ProjectRouteTabs.Trigger key={id} value={id} className={TAB_TRIGGER_CLASS}>
                {TABS[id].label(tLocalize)}
              </ProjectRouteTabs.Trigger>
            ))}
          </ProjectRouteTabs.List>
        </div>

        {tabs.map((id) => (
          <ProjectRouteTabs.Content key={id} value={id} className={TABS[id].contentClass}>
            {TABS[id].render({ projectId, myRole: project.my_role ?? undefined })}
          </ProjectRouteTabs.Content>
        ))}
      </ProjectRouteTabs>
    </div>
  );
}
